import { observer } from 'mobx-react-lite';

import { Button } from '@/components/Button';
import { sprintHint } from '@/tracker/entities/sprint';
import { searchActiveUsers } from '@/tracker/entities/user';
import { TaskStatusRef, TaskTypeRef } from '@/tracker/types';
import { FilterChip } from '@/tracker/ui/FilterChip';
import {
  getDisplayName,
  getId,
  getKey,
  getName,
  getQueueLabel,
} from '@/tracker/utils/optionGetters';

import { TaskFilterField, TaskFiltersModel } from '../TaskFiltersModel';
import { searchQueues, searchSprints, usePriorities, useTaskTypes } from '../dictionaries';

import s from './TaskFilterBar.module.scss';

type Props = {
  model: TaskFiltersModel;
  /** Какие плашки показать и в каком порядке */
  fields: TaskFilterField[];
  /** Статусы для выбора: очереди или колонок доски */
  statuses?: TaskStatusRef[];
  /** Типы очереди. Не переданы — общий справочник: на доске задачи разных очередей */
  types?: TaskTypeRef[];
};

/** Фильтры-плашки над списком задач или доской и «Сбросить», как в Яндекс Трекере */
const TaskFilterBar: React.FC<Props> = observer(({ model, fields, statuses = [], types }) => {
  const priorities = usePriorities(fields.includes('priorities'));
  const allTypes = useTaskTypes(fields.includes('types') && !types);
  const { values } = model;

  const renderChip = (field: TaskFilterField) => {
    switch (field) {
      case 'statuses':
        return (
          <FilterChip
            key={field}
            label="Статус"
            value={values.statuses}
            onChange={(value) => model.set({ field, value })}
            options={statuses}
            getId={getKey}
            getLabel={getName}
          />
        );

      case 'types':
        return (
          <FilterChip
            key={field}
            label="Тип"
            value={values.types}
            onChange={(value) => model.set({ field, value })}
            options={types ?? allTypes}
            getId={getKey}
            getLabel={getName}
          />
        );

      case 'priorities':
        return (
          <FilterChip
            key={field}
            label="Приоритет"
            value={values.priorities}
            onChange={(value) => model.set({ field, value })}
            options={priorities}
            getId={getKey}
            getLabel={getName}
          />
        );

      case 'assignees':
        return (
          <FilterChip
            key={field}
            label="Исполнитель"
            value={values.assignees}
            onChange={(value) => model.set({ field, value })}
            search={searchActiveUsers}
            getId={getId}
            getLabel={getDisplayName}
          />
        );

      case 'authors':
        return (
          <FilterChip
            key={field}
            label="Автор"
            value={values.authors}
            onChange={(value) => model.set({ field, value })}
            search={searchActiveUsers}
            getId={getId}
            getLabel={getDisplayName}
          />
        );

      case 'sprints':
        return (
          <FilterChip
            key={field}
            label="Спринт"
            value={values.sprints}
            onChange={(value) => model.set({ field, value })}
            search={searchSprints}
            getId={getId}
            getLabel={getName}
            getHint={sprintHint}
          />
        );

      case 'queues':
        return (
          <FilterChip
            key={field}
            label="Очередь"
            value={values.queues}
            onChange={(value) => model.set({ field, value })}
            search={searchQueues}
            getId={getKey}
            getLabel={getQueueLabel}
          />
        );
    }
  };

  return (
    <div className={s.bar}>
      {fields.map(renderChip)}
      {model.hasValues && (
        <Button variant="text" size="small" onClick={model.reset}>
          Сбросить
        </Button>
      )}
    </div>
  );
});

export { TaskFilterBar };
